// Shipping Results Display Component
(function() {
  'use strict';
  
  // Avoid duplicate registration
  if (customElements.get('xo-cart-shipping-rates-results') || customElements.get('xo-cart-shipping-rates-success')) {
    return;
  }

  // Global state store for shipping rates
  window.shippingRatesState = {
    status: 'idle',
    shippingRates: [],
    errorMessages: []
  };

  // Intercept ALL fetch requests to find shipping-related APIs
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const [url, options] = args;
    
    if (url && url.includes('/cart/shipping_rates.json')) {
      // Update state to loading
      window.shippingRatesState.status = 'loading';
      window.dispatchEvent(new CustomEvent('shipping-rates-state-change', {
        detail: window.shippingRatesState
      }));
      
      return originalFetch.apply(this, args)
        .then(response => {
          // Handle both success and error responses
          return response.clone().json().then(data => {
            if (!response.ok) {
              // Check for error messages in the response body
              let errorMessages = [];
              if (data.zip && Array.isArray(data.zip)) {
                errorMessages = data.zip;
              } else if (data.errors) {
                errorMessages = Array.isArray(data.errors) ? data.errors : [data.errors];
              } else if (data.message) {
                errorMessages = [data.message];
              } else {
                errorMessages = [`Request failed: ${response.statusText}`];
              }
              
              window.shippingRatesState.status = 'error';
              window.shippingRatesState.shippingRates = [];
              window.shippingRatesState.errorMessages = errorMessages;
              
              window.dispatchEvent(new CustomEvent('shipping-rates-state-change', {
                detail: window.shippingRatesState
              }));
              
              return response; // Don't throw, we handled the error
            }
            
            // Handle success responses
            // Check for errors first (even in 200 responses)
            if (data.zip && Array.isArray(data.zip)) {
              window.shippingRatesState.status = 'error';
              window.shippingRatesState.shippingRates = [];
              window.shippingRatesState.errorMessages = data.zip;
            }
            // Check if the response contains shipping_rates
            else if (data.shipping_rates && Array.isArray(data.shipping_rates)) {
              window.shippingRatesState.status = 'success';
              window.shippingRatesState.shippingRates = data.shipping_rates;
              window.shippingRatesState.errorMessages = [];
            } else if (data[0] && typeof data[0] === 'object') {
              // Handle case where shipping rates are returned as direct array
              window.shippingRatesState.status = 'success';
              window.shippingRatesState.shippingRates = Array.isArray(data) ? data : [data];
              window.shippingRatesState.errorMessages = [];
            } else {
              window.shippingRatesState.status = 'error';
              window.shippingRatesState.shippingRates = [];
              window.shippingRatesState.errorMessages = data.errors || ['No shipping rates available'];
            }
            
            window.dispatchEvent(new CustomEvent('shipping-rates-state-change', {
              detail: window.shippingRatesState
            }));
            
            return response;
          });
        })
        .catch(error => {
          window.shippingRatesState.status = 'error';
          window.shippingRatesState.errorMessages = ['Network error: ' + error.message];
          window.dispatchEvent(new CustomEvent('shipping-rates-state-change', {
            detail: window.shippingRatesState
          }));
          throw error;
        });
    }
    
    return originalFetch.apply(this, args);
  };

  // Success component for displaying shipping calculation success message
  class XOCartShippingRatesSuccess extends HTMLElement {
    constructor() {
      super();
      this.handleStateChange = this.handleStateChange.bind(this);
    }

    connectedCallback() {
      // Listen for our custom state change events
      window.addEventListener('shipping-rates-state-change', this.handleStateChange);
      
      // Check current state immediately
      this.renderSuccess(window.shippingRatesState.status, window.shippingRatesState.shippingRates, window.shippingRatesState.errorMessages);
    }

    disconnectedCallback() {
      window.removeEventListener('shipping-rates-state-change', this.handleStateChange);
    }

    handleStateChange(event) {
      const { status, shippingRates, errorMessages } = event.detail;
      this.renderSuccess(status, shippingRates, errorMessages);
    }

    renderSuccess(status, shippingRates, errorMessages = []) {
      // Clear error element when successful
      const errorElement = document.querySelector('xo-cart-shipping-rates-error');
      
      if (status === 'error' && errorMessages && errorMessages.length > 0) {
        // Show error messages in error element
        if (errorElement) {
          errorElement.innerHTML = errorMessages.join(', ');
        }
        this.innerHTML = ''; // Clear success content
      } else if (errorElement && status === 'success') {
        errorElement.innerHTML = '';
        
        if (shippingRates && shippingRates.length > 0) {
          const count = shippingRates.length;
          const freeCount = shippingRates.filter(rate => {
            const price = parseFloat(rate.price);
            return price === 0;
          }).length;
          
          let message = `Found ${count} shipping option${count !== 1 ? 's' : ''}`;
          if (freeCount > 0) {
            message += ` (including ${freeCount} free shipping option${freeCount !== 1 ? 's' : ''})`;
          }
          
          this.innerHTML = ``;
        } else {
          this.innerHTML = '';
        }
      } else if (status === 'loading') {
        this.innerHTML = '<div class="xo-cart-shipping-rates__success-loading">Calculating shipping rates...</div>';
      } else {
        this.innerHTML = '';
      }
    }
  }

  class XOCartShippingRatesResults extends HTMLElement {
    constructor() {
      super();
      this.handleStateChange = this.handleStateChange.bind(this);
    }

    connectedCallback() {
      this.innerHTML = '';
      
      // Listen for our custom state change events
      window.addEventListener('shipping-rates-state-change', this.handleStateChange);
      
      // Check current state immediately
      this.renderResults(window.shippingRatesState.status, window.shippingRatesState.shippingRates);
    }

    disconnectedCallback() {
      window.removeEventListener('shipping-rates-state-change', this.handleStateChange);
    }

    handleStateChange(event) {
      const { status, shippingRates } = event.detail;
      this.renderResults(status, shippingRates);
    }

    renderResults(status, shippingRates) {
      // Clear error element when rendering results
      const errorElement = document.querySelector('xo-cart-shipping-rates-error');
      if (errorElement && (status === 'success' || status === 'loading')) {
        errorElement.innerHTML = '';
      }

      if (status === 'loading') {
        this.innerHTML = '<div class="xo-cart-shipping-rates__results-loading">Calculating shipping rates...</div>';
        return;
      }

      if (status === 'success' && shippingRates && shippingRates.length > 0) {
        this.innerHTML = this.createShippingRatesList(shippingRates);
        this.addEventListeners();
      } else if (status === 'success' && (!shippingRates || shippingRates.length === 0)) {
        this.innerHTML = '<div class="xo-cart-shipping-rates__results-empty">No shipping options available for this address.</div>';
      } else if (status === 'error') {
        this.innerHTML = '';
      }
    }

    createShippingRatesList(shippingRates) {
      const ratesHTML = shippingRates.map((rate, index) => {
        const formattedPrice = this.formatPrice(rate.price);
        const isSelected = index === 0; // Select first option by default
        const isFree = parseFloat(rate.price) === 0;
        
        return `
          <div class="xo-cart-shipping-rates__option ${isSelected ? 'selected' : ''} ${isFree ? 'free-shipping' : ''}" data-rate-id="${rate.code || rate.id || index}">
            <label class="xo-cart-shipping-rates__option-label">
              <span class="xo-cart-shipping-rates__option-content">
                <span class="xo-cart-shipping-rates__option-name">${rate.name || rate.presentment_name || 'Shipping'} - ${formattedPrice}</span>
              </span>
            </label>
          </div>
        `;
      }).join('');

      return `
        <div class="xo-cart-shipping-rates__options">
          ${ratesHTML}
        </div>
      `;
    }

    formatPrice(priceString) {
      const price = parseFloat(priceString);
      
      // Handle free shipping
      if (price === 0) {
        return 'Free';
      }
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'AUD'
      }).format(price);
    }

    addEventListeners() {
      const radioButtons = this.querySelectorAll('input[name="shipping_rate"]');
      radioButtons.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.handleShippingSelection(e.target);
        });
      });
    }

    handleShippingSelection(selectedRadio) {
      // Update visual selection
      this.querySelectorAll('.xo-cart-shipping-rates__option').forEach(option => {
        option.classList.remove('selected');
      });
      selectedRadio.closest('.xo-cart-shipping-rates__option').classList.add('selected');

      // Trigger custom event for other components to listen
      this.dispatchEvent(new CustomEvent('shipping-rate-selected', {
        detail: {
          rateId: selectedRadio.value,
          price: selectedRadio.dataset.price,
          name: selectedRadio.closest('.xo-cart-shipping-rates__option-label').querySelector('.xo-cart-shipping-rates__option-name').textContent
        },
        bubbles: true
      }));
    }
  }

  // Register the custom elements
  customElements.define('xo-cart-shipping-rates-success', XOCartShippingRatesSuccess);
  customElements.define('xo-cart-shipping-rates-results', XOCartShippingRatesResults);

})();